import fileSize from 'filesize';

/** FileStore is the authority (Tika sniff, 100 MB cap); the extension stands in for the content it still sniffs. */
export type FileStoreKind = 'image' | 'video' | 'document';

export const FILE_STORE_EXTENSIONS: Readonly<Record<FileStoreKind, readonly string[]>> = {
    image: ['.jpeg', '.jpg', '.png'],
    video: ['.m4v', '.mp4', '.3gp', '.mov'],
    document: ['.pdf', '.doc', '.docx', '.ppt', '.pptx'],
};

/** Every extension FileStore keeps, for a picker that takes any kind. */
export const FILE_STORE_ACCEPTED_EXTENSIONS: readonly string[] = Object.values(FILE_STORE_EXTENSIONS).flat();

/** FileStore's own limit (`BaseFileProcessor.processFile`), 100 MB. */
export const FILE_STORE_MAX_BYTES = 104857600;

/** `upload.filename VARCHAR(200)` holds `<stem>-<13-digit ms>.<ext>`, 14 characters more than the name picked. */
const FILE_STORE_MAX_NAME_LENGTH = 200 - 14;

const ALL_KINDS: readonly FileStoreKind[] = ['image', 'video', 'document'];

/** How each kind is named to a person, in the order the kinds are listed. */
const KIND_WORDING: Record<FileStoreKind, string> = {
    image: 'a JPEG or PNG image',
    video: 'an MP4, MOV, M4V or 3GP video',
    document: 'a PDF, Word or PowerPoint document',
};

/** Which kind FileStore would store a file as, by its name, or undefined when it would refuse it. */
export const fileStoreKindOf = (filename: string): FileStoreKind | undefined => {
    const lower = filename.toLowerCase();
    return ALL_KINDS.find((kind) => FILE_STORE_EXTENSIONS[kind].some((ext) => lower.endsWith(ext)));
};

/** A comma-joined `accept` value: the extensions of the file's kind, or every kind FileStore keeps when it has none. */
export const fileStoreAcceptFor = (filename: string): string => {
    const kind = fileStoreKindOf(filename);
    return (kind ? FILE_STORE_EXTENSIONS[kind] : FILE_STORE_ACCEPTED_EXTENSIONS).join(',');
};

/** "a, b, or c", for the kinds a surface takes. */
const listKinds = (kinds: readonly FileStoreKind[]): string => {
    const words = ALL_KINDS.filter((kind) => kinds.includes(kind)).map((kind) => KIND_WORDING[kind]);
    if (words.length <= 1) return words.join('');
    return `${words.slice(0, -1).join(', ')}, or ${words[words.length - 1]}`;
};

const isHeic = (filename: string) => /\.(heic|heif)$/i.test(filename);

/** `kinds` narrows the message for a surface that takes fewer kinds; the type is checked first, as size is moot if it cannot be stored. */
export const describeFileStoreRefusal = (
    file: { name: string; size: number },
    kinds: readonly FileStoreKind[] = ALL_KINDS
): string | undefined => {
    const kind = fileStoreKindOf(file.name);

    if (!kind || !kinds.includes(kind)) {
        // Named on its own: it is what an iPhone shoots by default, and the way out is not obvious.
        if (isHeic(file.name) && kinds.includes('image')) {
            return `"${file.name}" is a HEIC photo, which cannot be attached. Save it as a JPEG or PNG and attach that.`;
        }
        return `"${file.name}" cannot be attached. Use ${listKinds(kinds)}.`;
    }
    if (file.size === 0) {
        return `"${file.name}" is empty, so it cannot be attached.`;
    }
    if (file.name.length > FILE_STORE_MAX_NAME_LENGTH) {
        return `"${file.name}" has too long a name to attach. Rename it to ${FILE_STORE_MAX_NAME_LENGTH} characters or fewer and attach it again.`;
    }
    if (file.size > FILE_STORE_MAX_BYTES) {
        return `"${file.name}" is larger than the ${fileSize(FILE_STORE_MAX_BYTES, {
            base: 2,
            round: 0,
        })} limit, so it cannot be attached.`;
    }
    return undefined;
};

/** Thrown by an upload function before any bytes leave, when FileStore would refuse the file anyway. */
export class FileStoreRefusedError extends Error {
    name = 'FileStoreRefusedError';
}

/** The last check before an upload: a backstop for a path that did not refuse the file when it was picked. */
export const assertFileStoreAccepts = (file: { name: string; size: number }): void => {
    const refusal = describeFileStoreRefusal(file);
    if (refusal) {
        throw new FileStoreRefusedError(refusal);
    }
};
